import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {LegacyMeasurement} from 'constants/MeasurementConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  concentrationUnitTypePredicate,
  containerQuantityUnitTypePredicate,
  getUnitFromCacheBySymbol,
  volumeUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import ErrorMessage from 'ui-library/ErrorMessage';
import NumericInput from 'ui-library/inputs/NumericInput';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {
  PresentationalSearchableUnitDropdown,
  SearchableUnitDropdownProps,
} from 'units/SearchableUnitDropdown';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';
import * as Keyboard from 'util/Keyboard';
import {NumericInputChangeEvent} from 'util/Types';

interface MeasurementFormProps {
  error?: string;
  measurement: LegacyMeasurement | undefined;
  /** `val` is the units */
  onChangeUnits: ({val}: {val: string | null}) => void;
  // `NumericInput` passes a modified HTML event with the number value in `e.target.numericValue`!
  // (the original string value is in `e.target.value`)
  onChangeValue: (e: NumericInputChangeEvent) => void;
  onEnter?: () => void;
  validUnits: Array<string>;
}

function ValueInput(
  props: Pick<MeasurementFormProps, 'error' | 'onChangeValue' | 'onEnter' | 'measurement'>
): JSX.Element {
  return (
    <NumericInput
      className={classNames('u-small-spacing-right', css(styles.flexWrap), {
        'fieldInput-input--error': Boolean(props.error),
      })}
      min={0}
      onChange={props.onChangeValue}
      onKeyDown={Keyboard.callOnEnter(props.onEnter)}
      required={Boolean(props.measurement?.units)}
      placeholder="Value"
      value={props.measurement?.value}
    />
  );
}

const styles = StyleSheet.create({
  flexWrap: {flex: '1 1 70px'},
});

export enum MeasurementType {
  VOLUME = 'VOLUME',
  QUANTITY = 'QUANTITY',
  CONCENTRATION = 'CONCENTRATION',
}

export const FILTER_PREDICATES_BY_MEASUREMENT_TYPE = {
  [MeasurementType.QUANTITY]: containerQuantityUnitTypePredicate,
  [MeasurementType.CONCENTRATION]: concentrationUnitTypePredicate,
  [MeasurementType.VOLUME]: volumeUnitTypePredicate,
};

type NewProps = Omit<MeasurementFormProps, 'validUnits'> & {
  measurementType: MeasurementType;
  customUnitTypeFilter?: SearchableUnitDropdownProps['unitTypeFilter'] | null;
};

export function NewMeasurementForm(props: NewProps): JSX.Element {
  return (
    <BenchlingQuery<UnitTypesQuery>
      query={UNIT_TYPES_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {({unitTypes}) => (
        <div>
          <div className="u-vertical-stretch">
            <ValueInput {...props} />
            <div style={{width: '90px'}}>
              <PresentationalSearchableUnitDropdown
                unitTypes={unitTypes}
                displaySelectionAsSymbol={true}
                includeNone={true}
                selectedId={
                  props.measurement?.units ? getUnitFromCacheBySymbol(props.measurement.units)?.id : null
                }
                onSelect={(u) => {
                  props.onChangeUnits({val: u?.symbol ?? null});
                }}
                unitTypeFilter={
                  props.customUnitTypeFilter ?? FILTER_PREDICATES_BY_MEASUREMENT_TYPE[props.measurementType]
                }
              />
            </div>
          </div>
          {props.error && <ErrorMessage error={props.error} />}
        </div>
      )}
    </BenchlingQuery>
  );
}
